// perlu.view — menu "⚠️ Perlu Diperhatikan Segera" + panel dipakai ulang di Dashboard.
import { cabangOpsi, opsiCabangSyncHTML } from '@core/cabangOpsi';
// Port renderPerluDiperhatikan/perluPanel/perluRow v0.18 (baris 526–534).
import { esc, rp, fillTpl, pecahUnit, tglID } from '@domain/format';
import { currentUser } from '@core/session';
import { readDraft, clearDraft } from '@core/draft';
import { goto } from '@core/nav';
import { kebijakan, waTpl } from '@core/settings';
import { openWA } from '@core/wa';
import { toast } from '@ui/toast';
import { kirimPengingat } from '@modules/penagihan/penagihan.repo';
import { listMenungguTerima, type MenungguTerima } from '@modules/sparepart/sparepart.repo';
import { renderPenagihan } from '@modules/penagihan/penagihan.view';
import { bisaBuka } from '@core/perms';
import { piutangTiket } from '@domain/money';
import { openDetail } from '@modules/queue/queue.view';
import { listKaryawan } from '@modules/karyawan/karyawan.repo';
import { fetchPerluTickets } from './perlu.repo';
import { perluBuild, perluCount, type PerluCat, type PerluItem } from './perlu.build';

// Menarik SEMUA tiket + seluruh log-nya itu mahal, dan sekarang dipanggil dua kali tiap
// membuka menu ini (badge sidebar + isi layar) serta tiap pindah menu. Simpan sebentar.
// ponytail: cache 60 detik di memori; kalau kelak perlu seketika, pakai realtime Supabase.
let cachePerlu: { at: number; role: string; v: PerluCat[] } | null = null;

/** Fetch + susun kategori untuk peran user saat ini. `force` melewati cache (tombol Refresh). */
export async function loadPerlu(force = false): Promise<PerluCat[]> {
  const u = currentUser(); if (!u) return [];
  if (!force && cachePerlu && cachePerlu.role === u.role && Date.now() - cachePerlu.at < 60_000) return cachePerlu.v;
  const tickets = await fetchPerluTickets();
  const k = kebijakan();
  const v = perluBuild(tickets, u.role, { slaOn: k.slaOn, slaCfg: k.slaCfg, ambangBelum: k.ambangBelum, verifikasiBayarMenit: k.slaVerifikasiBayarMenit, verifikasiRefundMenit: k.slaVerifikasiRefundMenit, penagihanEskalasiHari: k.slaPenagihanEskalasiHari, graceEstimasiHari: k.graceEstimasiHari, staleKonfirmasiHari: k.staleKonfirmasiHari });
  // Sparepart yang menunggu konfirmasi penerimaan (0128) — sumbernya tabel request, bukan tabel
  // tiket, jadi disisipkan di sini alih-alih memaksa perluBuild menerima bentuk data lain.
  // Penting ditandai: selama belum dikonfirmasi, modal tiket & hutang supplier BELUM diakui.
  if (['Teknisi', 'CS', 'SPV', 'Manager', 'Owner'].includes(u.role)) {
    try {
      const nunggu: MenungguTerima[] = (await listMenungguTerima())
        .filter((x) => (u.role === 'Teknisi' ? x.teknisi === u.nama : true));
      if (nunggu.length) {
        const jamBatas = Math.max(1, k.autoTerimaJam);
        v.unshift({
          key: 'terima_part', label: '📦 Sparepart menunggu konfirmasi diterima', color: 'var(--warn)',
          items: nunggu.map((x) => ({
            t: { no: x.ticket_no, outlet: x.outlet, teknisi: x.teknisi } as PerluCat['items'][number]['t'],
            urgent: x.jamLalu >= jamBatas,
            reason: `${x.supplier_nama ?? 'Supplier'} menyerahkan ${x.jamLalu} jam lalu · ${x.jamLalu >= jamBatas ? 'akan/sudah otomatis diterima' : `otomatis diterima setelah ${jamBatas} jam`}`,
          })),
        });
      }
    } catch { /* tanda opsional — jangan menggagalkan menu */ }
  }
  cachePerlu = { at: Date.now(), role: u.role, v };
  return v;
}

// PLU1: tombol WA inline (tagih/siap → pelanggan) + (O/M/SPV) nudge TEKNISI di item kerja/sla → reminder tambahan (Apo).
function perluRow(it: PerluItem, catKey: string, staffWa?: Map<string, string>): string {
  const border = it.urgent ? 'border-left:3px solid var(--danger)' : 'border-left:3px solid var(--line)';
  const wa = it.t.custWa;
  let waBtn = '';
  const un = pecahUnit(it.t.unit);
  if (wa && catKey === 'tagih') {
    const msg = fillTpl(waTpl().tagih, {
      nama: it.t.custNama, no: it.t.no, unit: it.t.unit, merk: un.merk, tipe: un.tipe,
      sisa: rp(piutangTiket(it.t.ticket)), jt: it.t.jatuhTempo ? ` (jatuh tempo ${tglID(it.t.jatuhTempo)})` : '',
    });
    waBtn = `<button class="btn perlu-wa" style="padding:2px 8px;font-size:11.5px" data-wa="${esc(wa)}" data-no="${esc(it.t.no)}" data-jenis="Tagih piutang" data-msg="${esc(msg)}">💬 Tagih</button>`;
  } else if (wa && catKey === 'siap') {
    const msg = fillTpl(waTpl().siapLaporan, { nama: it.t.custNama, no: it.t.no, unit: it.t.unit, merk: un.merk, tipe: un.tipe });
    waBtn = `<button class="btn perlu-wa" style="padding:2px 8px;font-size:11.5px" data-wa="${esc(wa)}" data-no="${esc(it.t.no)}" data-jenis="Pengingat ambil unit" data-msg="${esc(msg)}">💬 Siap</button>`;
  }
  // Nudge staff (teknisi) — hanya O/M/SPV (staffWa dikirim) & kategori kerja/sla, teknisi ber-WA.
  let staffBtn = '';
  const tekWa = it.t.teknisi ? staffWa?.get(it.t.teknisi) : undefined;
  if (staffWa && (catKey === 'kerja' || catKey === 'sla') && it.t.teknisi && tekWa) {
    const msg = `Halo ${it.t.teknisi}, mohon tindak lanjuti servisan ${it.t.no} (${it.t.unit}) — ${it.reason.replace(/<[^>]*>/g, '')}. Terima kasih — iFiCs`;
    staffBtn = `<button class="btn perlu-wa" style="padding:2px 8px;font-size:11.5px;border-color:var(--blue);color:var(--blue)" data-wa="${esc(tekWa)}" data-msg="${esc(msg)}">💬 ${esc(it.t.teknisi)}</button>`;
  }
  // Nomor tiket dibuat <button> agar baris ini bisa dibuka lewat KEYBOARD (dulu div klik = tak bisa
  // di-Tab sama sekali). Barisnya sendiri tak bisa jadi <button> karena memuat tombol WA di dalamnya
  // (nested button = HTML tak sah); klik tombol ini menggelembung ke handler baris yang sudah ada.
  return `<div class="hist perlu-row" style="cursor:pointer;${border}" data-detail="${esc(it.t.no)}">
    <div class="h1"><span><button type="button" class="lnk-btn" aria-label="Buka servis ${esc(it.t.no)} — ${esc(it.reason.replace(/<[^>]*>/g, ''))}"><b>${esc(it.t.no)}</b></button> · ${esc(it.t.custNama)}</span><span style="display:flex;gap:6px;align-items:center">${staffBtn}${waBtn}<span class="outlet-tag">${esc(it.t.unit)}</span></span></div>
    <div class="h2">${esc(it.reason)}</div></div>`;
}

/** HTML panel Perlu Diperhatikan. `compact` = top-5 per kategori (untuk Dashboard). staffWa = map nama→WA (nudge teknisi, O/M/SPV). */
/** Isian Terima Servis yang belum diselesaikan (mati lampu/koneksi putus) — muncul di panel
 *  Perlu Diperhatikan supaya tak terlupa. Draft tersimpan di perangkat, bukan di server. */
function draftReminderHTML(): string {
  const ids: string[] = [];
  const uid = currentUser()?.id;
  if (!uid) return '';
  const pref = `ifics-draft:intake:${uid}:`;   // hanya draft MILIK pengguna ini (perangkat bisa dipakai bergantian)
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(pref)) ids.push(k.slice('ifics-draft:'.length));
    }
  } catch { return ''; }
  // `at` diberi cadangan: draft dari versi lama (atau yang isinya rusak) pernah membuat seluruh
  // panel gagal dirender gara-gara satu tanggal kosong — satu isian setengah jadi tak boleh
  // menjatuhkan daftar pekerjaan orang.
  const rows = ids.map((id) => { const d = readDraft(id); return d ? { id, at: d.meta.at || '' } : null; })
    .filter(Boolean) as { id: string; at: string }[];
  if (!rows.length) return '';
  return `<div style="font-weight:600;color:var(--warn);margin:10px 0 4px">📝 Isian belum selesai <span class="pill" style="background:var(--ice);color:var(--navy)">${rows.length}</span></div>`
    + rows.map((r) => `<div class="hist" style="border-left:3px solid var(--warn)"><div class="h1">
        <span>Terima Servis — cabang <b>${esc(r.id.split(':').pop() ?? '')}</b></span>
        <span class="outlet-tag">${esc(r.at ? r.at.slice(0, 16).replace('T', ' ') : 'tanpa tanggal')}</span></div>
      <div class="h2">Isian tersimpan di perangkat ini tapi <b>belum jadi tiket</b>.</div>
      <div style="margin-top:7px;display:flex;gap:6px;flex-wrap:wrap">
        <button class="btn primary" style="padding:3px 11px" data-draflanjut="${esc(r.id)}">📝 Lanjutkan</button>
        <button class="btn" style="padding:3px 11px;border-color:var(--danger);color:var(--danger)" data-drafbuang="${esc(r.id)}">🗑️ Buang</button>
      </div></div>`).join('');
}

export function perluPanelHTML(cats: PerluCat[], compact: boolean, staffWa?: Map<string, string>): string {
  const draf = draftReminderHTML();
  if (!cats.length) return draf || '<div class="scope-note" style="padding:10px">✅ Tidak ada tugas mendesak — semua on-track. 🎉</div>';
  return draf + cats.map((c) => {
    const items = compact ? c.items.slice(0, 5) : c.items;
    return `<div style="font-weight:600;color:${c.color};margin:10px 0 4px">${esc(c.label)} <span class="pill" style="background:var(--ice);color:var(--navy)">${c.items.length}</span></div>` +
      items.map((it) => perluRow(it, c.key, staffWa)).join('') +
      ((compact && c.items.length > 5) ? `<div style="font-size:12px;color:var(--muted);margin:2px 0 0">+${c.items.length - 5} lagi — buka tab "Perlu Diperhatikan Segera"</div>` : '');
  }).join('');
}

/** Pasang handler: klik baris → buka DETAIL tiket; tombol 💬 WA → openWA (dev=dummy). */
export function wirePerluRows(root: HTMLElement, refresh: () => void): void {
  root.querySelectorAll<HTMLElement>('.perlu-row[data-detail]').forEach((el) =>
    el.addEventListener('click', () => void openDetail(el.dataset.detail ?? '', refresh)));
  root.querySelectorAll<HTMLElement>('.perlu-wa').forEach((b) =>
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      // Pesan ke PELANGGAN dicatat (jejak penagihan); nudge ke staf tidak — bukan penagihan.
      const no = b.dataset.no, jenis = b.dataset.jenis;
      if (no && jenis) kirimPengingat(no, b.dataset.wa, b.dataset.msg ?? '', jenis, toast);
      else openWA(b.dataset.wa, b.dataset.msg ?? '');
    }));

  // Isian Terima Servis yang belum jadi tiket. Tersimpan di PERANGKAT (localStorage), bukan di
  // database — jadi Reset Data tidak menyentuhnya sama sekali, dan sisanya tetap tampil setelah
  // seluruh data dihapus. Satu-satunya cara membuangnya ya dari sini.
  root.querySelectorAll<HTMLElement>('button[data-draflanjut]').forEach((b) =>
    b.addEventListener('click', (e) => { e.stopPropagation(); goto('terima'); }));
  root.querySelectorAll<HTMLElement>('button[data-drafbuang]').forEach((b) =>
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = b.dataset['drafbuang']; if (!id) return;
      if (!confirm('Buang isian yang belum selesai ini? Isian yang belum jadi tiket akan hilang.')) return;
      clearDraft(id);
      toast('🗑️ Isian dibuang');
      refresh();
    }));
}

/**
 * Jumlah untuk badge 💬 di sidebar: berapa hal yang menunggu dikerjakan orang ini.
 * Dipakai shell tiap pindah menu — Apo memakai tanda ini sebagai pengganti panel Dashboard
 * yang dihapus, jadi angkanya harus mewakili SEMUA sub-tab, bukan hanya tugas.
 */
export async function perluActionCount(): Promise<number> {
  // Hanya yang MENDESAK. Kalau semua item dihitung, di toko yang jalan normal angkanya tak
  // pernah nol — badge merah permanen berhenti dibaca orang, padahal ini pengganti panel
  // Dashboard yang dihapus.
  try { return (await loadPerlu()).reduce((a, c) => a + c.items.filter((i) => i.urgent).length, 0); } catch { return 0; }
}

// Segregasi (Apo): O/M/SPV → 2 tab. Managerial = SLA/eskalasi (kategori 'sla'); Pantauan Cabang = sub-peran (kerja/konfirmasi/siap/verif/tagih).
const MGR_ROLES_P = ['Owner', 'Manager', 'SPV'];
const MGR_CATS = ['sla'];

// Menu Penagihan dilebur ke sini sebagai SUB-TAB (permintaan Apo 21 Jul): satu tempat untuk
// "apa yang harus saya kerjakan", bukan dua menu yang isinya saling bersinggungan.
const TAGIH_ROLES = ['Owner', 'Manager', 'Admin Finance', 'SPV', 'CS'];
type PTab = 'mgr' | 'scope' | 'tugas' | 'tagih' | 'ambil';
let pTab: PTab | null = null;   // ingat tab terakhir selama sesi

let perluCab = '';

export async function renderPerluDiperhatikan(root: HTMLElement): Promise<void> {
  const u = currentUser(); if (!u) return;
  const JUDUL = '⚠️ Perlu Diperhatikan';
  root.innerHTML = `<div class="ttl">${JUDUL}</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let cats: PerluCat[];
  await cabangOpsi();
  try { cats = await loadPerlu(); }
  catch (e) { root.innerHTML = `<div class="ttl">${JUDUL}</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const mgrView = MGR_ROLES_P.includes(u.role);
  // Peran + Hak Akses: baris di layar Hak Akses harus benar-benar mematikan tabnya,
  // bukan sekadar tercatat.
  const bolehTagih = TAGIH_ROLES.includes(u.role) && bisaBuka('perlu_diperhatikan.tagih');
  const bolehIngatkan = TAGIH_ROLES.includes(u.role) && bisaBuka('perlu_diperhatikan.ingatkan');
  // Peta nama→WA (nudge teknisi) hanya utk O/M/SPV.
  let staffWa: Map<string, string> | undefined;
  if (mgrView && bisaBuka('perlu_diperhatikan.nudge')) { try { const emps = await listKaryawan(); staffWa = new Map(emps.filter((e) => e.wa).map((e) => [e.nama, e.wa as string])); } catch { /* wa opsional */ } }

  // Saring per cabang. Dilakukan SESUDAH data dimuat (bukan di query) supaya angka lonceng &
  // hitungan tab tetap memakai seluruh wilayah yang jadi tanggung jawab pengguna.
  if (perluCab) cats = cats.map((c) => ({ ...c, items: c.items.filter((i) => i.t.outlet === perluCab) }));
  const mgrCats = cats.filter((c) => MGR_CATS.includes(c.key));
  const scopeCats = cats.filter((c) => !MGR_CATS.includes(c.key));
  const cnt = (cs: PerluCat[]): number => cs.reduce((a, c) => a + c.items.length, 0);
  const total = perluCount(cats);

  // Susunan tab per peran. Yang di depan = yang paling sering dikerjakan orang itu.
  const tabs: { k: PTab; l: string; n: number; merah?: boolean }[] = mgrView
    ? [{ k: 'mgr', l: '🎯 Tugas Managerial', n: cnt(mgrCats), merah: true }, { k: 'scope', l: '🏢 Pantauan Cabang', n: cnt(scopeCats) }]
    : [{ k: 'tugas', l: '📋 Tugas Saya', n: total, merah: true }];
  if (bolehTagih) tabs.push({ k: 'tagih', l: '📒 Tagih Piutang', n: -1 });
  if (bolehIngatkan) tabs.push({ k: 'ambil', l: '📦 Ingatkan Ambil Unit', n: -1 });

  // Tab awal: yang tersimpan dari kunjungan sebelumnya, kalau masih ada; kalau tidak, tab
  // pertama yang benar-benar ada isinya — supaya layar tak dibuka pada daftar kosong.
  const adaTab = (k: PTab | null): boolean => !!k && tabs.some((t) => t.k === k);
  const awal: PTab = adaTab(pTab) ? (pTab as PTab)
    : (tabs.find((t) => t.n > 0)?.k ?? tabs[0]?.k ?? 'tugas');
  pTab = awal;

  const pill = (t: { n: number; merah?: boolean }): string => (t.n > 0
    ? ` <span class="pill" style="background:${t.merah ? 'var(--dangerbg)' : 'var(--ice)'};color:${t.merah ? 'var(--danger)' : 'var(--navy)'}">${t.n}</span>` : '');
  const tabBar = `<div class="nt-tabs" style="border-radius:0">${tabs.map((t) =>
    `<button data-ptab="${t.k}" class="${awal === t.k ? 'active' : ''}">${t.l}${pill(t)}</button>`).join('')}</div>`;

  const catatan = mgrView
    ? '<b>Tugas Managerial</b> = eskalasi/SLA yang perlu Anda tangani · <b>Pantauan Cabang</b> = kerja sub-peran (pengawasan) · <b>Tagih & Ingatkan</b> = daftar kirim WA ke pelanggan. Klik baris → buka tiket; 💬 <b>nama teknisi</b> = ingatkan langsung via WA.'
    : 'Semua yang perlu Anda kerjakan hari ini ada di sini — diurut <b>prioritas</b> & <b>yang paling lama</b>. Klik baris untuk <b>membuka tiket</b>; tombol <b>💬</b> kirim WA.';

  root.innerHTML = `<div class="crumb">${esc(u.nama)} · ${esc(u.role)}</div>
    <div class="ttl">${JUDUL} ${total ? `<span class="pill" style="background:var(--dangerbg);color:var(--danger)">${total}</span>` : ''}</div>
    <div class="scope-note">${catatan} <button class="btn" style="padding:3px 10px;font-size:12px;margin-left:6px" id="perlu_refresh">🔄 Refresh</button></div>
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;margin:8px 0;flex-wrap:wrap">
      <label style="font-size:13px;color:var(--muted)">Cabang</label>
      <select id="perlu_cab">${opsiCabangSyncHTML(perluCab)}</select></div>
    ${tabBar}
    <div class="panel" id="perlu_pane" style="padding:12px"><div class="empty">Memuat…</div></div>`;

  root.querySelector('#perlu_cab')?.addEventListener('change', (e) => {
    perluCab = (e.target as HTMLSelectElement).value; void renderPerluDiperhatikan(root);
  });
  const pane = root.querySelector('#perlu_pane') as HTMLElement;
  const refresh = (): void => { cachePerlu = null; void renderPerluDiperhatikan(root); };
  root.querySelector('#perlu_refresh')?.addEventListener('click', refresh);

  const gambarTab = (k: PTab): void => {
    pTab = k;
    if (k === 'tagih' || k === 'ambil') {
      // Klik cepat dua tab di koneksi lambat: render yang lebih lama selesai belakangan dan
      // menimpa pane, sehingga isinya bukan tab yang aktif. Batalkan yang sudah kedaluwarsa.
      void renderPenagihan(pane, { tab: k, tanpaKepala: true }).then(() => { if (pTab !== k) gambarTab(pTab as PTab); });
      return;
    }
    const isi = k === 'mgr' ? mgrCats : (k === 'scope' ? scopeCats : cats);
    pane.innerHTML = perluPanelHTML(isi, false, k === 'tugas' ? undefined : staffWa);
    wirePerluRows(pane, refresh);
  };
  gambarTab(awal);
  root.querySelectorAll<HTMLElement>('button[data-ptab]').forEach((b) => b.addEventListener('click', () => {
    root.querySelectorAll('button[data-ptab]').forEach((x) => x.classList.remove('active'));
    b.classList.add('active');
    gambarTab(b.dataset['ptab'] as PTab);
  }));
}
